// ── BCHNostr — BCH Video hooks ────────────────────────────────────
//
// The Nostr read/write layer for video. Concerns are kept separate so a
// page only pays for what it renders:
//
//   useVideoDirectory     every published video (discovery)
//   useChannelVideos      one creator's videos (author-filtered)
//   useVideo              one specific video (author-filtered)
//   useVideoEngagement    tips / comments / reactions for a set of videos
//   useVideoBoosts        active promotions
//   useVideoPlaylists     a creator's playlists
//   usePlaylist           one playlist + its resolved videos
//   useRecommendations    the transparent scoring, wired to real signals
//   useVideoPublish       add / edit / remove a video
//
// TRUST BOUNDARY. Kind 34235 and 30005 are addressable and user-owned, so
// a `d` tag proves nothing: anyone may publish `d = <anything>`. Every read
// of a SPECIFIC video or playlist therefore filters by `authors`, and the
// unfiltered directory query attributes each event to its own signer and
// re-parses it. Boost events are likewise attributed to their signer, never
// to whoever the payload names.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Event } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import { useNostrPublish } from './useNostrPublish';
import { useAppContext } from './useAppContext';
import { useFollows } from './useFollows';
import {
  VIDEO_KIND, VIDEO_TAG, VIDEO_CAT_PREFIX,
  VIDEO_COMMENT_KIND, PLAYLIST_KIND, PLAYLIST_TAG,
  VIDEO_BOOST_KIND, VIDEO_BOOST_TAG, TIP_RECEIPT_KIND,
  parseVideo, parsePlaylist, activeVideoBoosts,
  buildVideoEvent, retireVideoEvent, buildPlaylistEvent, retirePlaylistEvent,
  videoCoordinate, parseVideoCoordinate, videoDTag,
  getCategoryInterests, watchedCoordinates, getWatchHistory,
  rankRecommended, rankTrending, EMPTY_ENGAGEMENT,
  type BCHVideo, type VideoPlaylist, type VideoBoostData,
  type VideoEngagement, type VideoDraft, type PlaylistDraft,
  type ScoredVideo, type RecommendationSignals, type HistoryEntry,
} from '../lib/video';

// ── Discovery ─────────────────────────────────────────────────────

export interface UseVideoDirectoryResult {
  videos: BCHVideo[];
  loading: boolean;
  refetch: () => void;
}

/**
 * Every published BCHNostr video, newest first.
 *
 * Public UGC, so no author filter — but one entry per (author, d) with
 * newest winning, because 34235 is addressable and relays legitimately
 * return several revisions of the same video.
 */
export function useVideoDirectory(category?: string): UseVideoDirectoryResult {
  const filters = useMemo(() => {
    const t = category && category !== 'all'
      ? [VIDEO_TAG, `${VIDEO_CAT_PREFIX}${category}`]
      : [VIDEO_TAG];
    return [{ kinds: [VIDEO_KIND], '#t': t, limit: 400 }];
  }, [category]);

  const { events, loading, refetch } = useNostrQuery({ filters, allRelays: true });

  const videos = useMemo(() => {
    const byKey = new Map<string, Event>();
    for (const ev of events) {
      const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
      if (!dTag) continue;
      // Key includes the pubkey: two creators may share a `d` tag, and
      // neither may overwrite the other.
      const key = `${ev.pubkey}:${dTag}`;
      const existing = byKey.get(key);
      if (!existing || ev.created_at > existing.created_at) byKey.set(key, ev);
    }
    return [...byKey.values()]
      .map(parseVideo)
      .filter((v): v is BCHVideo => v !== null)
      // A category filter rides an indexed tag, but a hostile event could
      // carry several — re-check client-side so the tab never lies.
      .filter(v => !category || category === 'all' || v.category === category)
      .sort((a, b) => b.publishedAt - a.publishedAt);
  }, [events, category]);

  return { videos, loading, refetch };
}

// ── One creator's channel ─────────────────────────────────────────

/** Videos published by `pubkey`. Author-filtered. */
export function useChannelVideos(pubkey: string | undefined): UseVideoDirectoryResult {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey
      ? [{ kinds: [VIDEO_KIND], authors: [pubkey], '#t': [VIDEO_TAG], limit: 300 }]
      : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const videos = useMemo(() => {
    const byDTag = new Map<string, Event>();
    for (const ev of events) {
      // Defence in depth: relays over-return, and this list is presented
      // as "this person's videos".
      if (pubkey && ev.pubkey !== pubkey) continue;
      const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
      if (!dTag) continue;
      const existing = byDTag.get(dTag);
      if (!existing || ev.created_at > existing.created_at) byDTag.set(dTag, ev);
    }
    return [...byDTag.values()]
      .map(parseVideo)
      .filter((v): v is BCHVideo => v !== null)
      .sort((a, b) => b.publishedAt - a.publishedAt);
  }, [events, pubkey]);

  return { videos, loading, refetch };
}

// ── One video ─────────────────────────────────────────────────────

export interface UseVideoResult {
  video: BCHVideo | null;
  loading: boolean;
  refetch: () => void;
}

/** Load one video by creator + `d` tag. Author-filtered. */
export function useVideo(
  pubkey: string | undefined, dTag: string | undefined,
): UseVideoResult {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey && dTag
      ? [{ kinds: [VIDEO_KIND], authors: [pubkey], '#d': [dTag], limit: 4 }]
      : [],
    enabled: !!pubkey && !!dTag,
    allRelays: true,
  });

  const video = useMemo(() => {
    const mine = events.filter(ev =>
      ev.pubkey === pubkey &&
      ev.tags.some(t => t[0] === 'd' && t[1] === dTag));
    if (mine.length === 0) return null;
    // Newest wins — 34235 is addressable.
    const newest = [...mine].sort((a, b) => b.created_at - a.created_at)[0];
    return parseVideo(newest);
  }, [events, pubkey, dTag]);

  return { video, loading, refetch };
}

// ── Engagement ────────────────────────────────────────────────────

/**
 * Tips, comments and reactions for a set of videos.
 *
 * One batched query per signal type rather than N per video, keyed on the
 * addressable coordinates. Everything here is relay-dependent and so is a
 * RANKING SIGNAL, never an authoritative ledger — the UI says so.
 */
export function useVideoEngagement(videos: BCHVideo[]): Map<string, VideoEngagement> {
  // Cap the batch: an `#a` filter with 500 values is refused by many relays.
  const coords = useMemo(
    () => videos.slice(0, 120).map(v => videoCoordinate(v.pubkey, v.id)),
    [videos],
  );
  const coordKey = useMemo(() => coords.join(','), [coords]);
  const coordList = useMemo(() => (coordKey ? coordKey.split(',') : []), [coordKey]);

  const { events } = useNostrQuery({
    filters: coordList.length > 0
      ? [
          // Comments and reactions address the video by coordinate.
          { kinds: [VIDEO_COMMENT_KIND, 7], '#a': coordList, limit: 1000 },
          // Tip receipts do too, when the tip was sent from a video page.
          { kinds: [TIP_RECEIPT_KIND], '#a': coordList, limit: 1000 },
        ]
      : [],
    enabled: coordList.length > 0,
    allRelays: true,
  });

  return useMemo(() => {
    const valid = new Set(coordList);
    const dayAgo = Math.floor(Date.now() / 1000) - 86400;

    interface Acc {
      tippers: Set<string>;
      tipSats: number;
      comments: number;
      reactors: Set<string>;
      participants: Set<string>;
      recent: number;
    }
    const acc = new Map<string, Acc>();
    const get = (coord: string): Acc => {
      let entry = acc.get(coord);
      if (!entry) {
        entry = {
          tippers: new Set(), tipSats: 0, comments: 0,
          reactors: new Set(), participants: new Set(), recent: 0,
        };
        acc.set(coord, entry);
      }
      return entry;
    };

    for (const ev of events) {
      // An event may carry several `a` tags; only ours count.
      for (const tag of ev.tags) {
        if (tag[0] !== 'a' || !tag[1] || !valid.has(tag[1])) continue;
        const entry = get(tag[1]);
        const target = parseVideoCoordinate(tag[1]);
        // Self-engagement is not engagement.
        const isSelf = !!target && target.pubkey === ev.pubkey;

        if (ev.kind === VIDEO_COMMENT_KIND) {
          entry.comments++;
          if (!isSelf) entry.participants.add(ev.pubkey);
        } else if (ev.kind === 7) {
          if (!isSelf) {
            entry.reactors.add(ev.pubkey);
            entry.participants.add(ev.pubkey);
          }
        } else if (ev.kind === TIP_RECEIPT_KIND) {
          let sats = parseInt(ev.tags.find(t => t[0] === 'amount')?.[1] || '', 10);
          if (!Number.isFinite(sats) || sats <= 0) {
            try {
              const parsed = JSON.parse(ev.content || '{}') as { amount_sats?: number };
              sats = Number(parsed.amount_sats) || 0;
            } catch { sats = 0; }
          }
          if (!isSelf && sats > 0) {
            entry.tipSats += sats;
            entry.tippers.add(ev.pubkey);
            entry.participants.add(ev.pubkey);
          }
        }
        if (ev.created_at >= dayAgo && !isSelf) entry.recent++;
        break;
      }
    }

    const out = new Map<string, VideoEngagement>();
    for (const [coord, entry] of acc) {
      out.set(coord, {
        uniqueTippers: entry.tippers.size,
        tipSats: entry.tipSats,
        comments: entry.comments,
        reactions: entry.reactors.size,
        uniqueParticipants: entry.participants.size,
        recentActivity: entry.recent,
      });
    }
    return out;
  }, [events, coordList]);
}

/** Engagement for a single video — a thin wrapper for the watch page. */
export function useSingleVideoEngagement(video: BCHVideo | null): VideoEngagement {
  const list = useMemo(() => (video ? [video] : []), [video]);
  const map = useVideoEngagement(list);
  if (!video) return EMPTY_ENGAGEMENT;
  return map.get(videoCoordinate(video.pubkey, video.id)) || EMPTY_ENGAGEMENT;
}

// ── Comments ──────────────────────────────────────────────────────

export interface UseVideoCommentsResult {
  comments: Event[];
  loading: boolean;
  refetch: () => void;
}

/**
 * NIP-22 comments (kind 1111) rooted on the video's addressable
 * coordinate. Any NIP-22 client addresses the same thread, and no video
 * bytes are ever duplicated into Nostr.
 */
export function useVideoComments(video: BCHVideo | null): UseVideoCommentsResult {
  const coord = video ? videoCoordinate(video.pubkey, video.id) : '';

  const { events, loading, refetch } = useNostrQuery({
    filters: coord
      ? [{ kinds: [VIDEO_COMMENT_KIND], '#A': [coord], limit: 300 }]
      : [],
    enabled: !!coord,
    allRelays: true,
  });

  const comments = useMemo(
    () => [...events].sort((a, b) => b.created_at - a.created_at),
    [events],
  );

  return { comments, loading, refetch };
}

// ── Promotion ─────────────────────────────────────────────────────

export interface UseVideoBoostsResult {
  boosts: VideoBoostData[];
  /** `<pubkey>:<dTag>` → the best active promotion for that video. */
  byVideo: Map<string, VideoBoostData>;
  loading: boolean;
}

/** Currently active video promotions, highest bid first. */
export function useVideoBoosts(): UseVideoBoostsResult {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 2 * 86400);
  const { events, loading } = useNostrQuery({
    filters: [{ kinds: [VIDEO_BOOST_KIND], '#t': [VIDEO_BOOST_TAG], since, limit: 150 }],
    allRelays: true,
  });

  const boosts = useMemo(() => activeVideoBoosts(events), [events]);

  const byVideo = useMemo(() => {
    const map = new Map<string, VideoBoostData>();
    for (const b of boosts) {
      if (!b.videoDTag) continue;
      const key = `${b.videoPubkey}:${b.videoDTag}`;
      const existing = map.get(key);
      if (!existing || b.priceSats > existing.priceSats) map.set(key, b);
    }
    return map;
  }, [boosts]);

  return { boosts, byVideo, loading };
}

// ── Playlists ─────────────────────────────────────────────────────

export interface UseVideoPlaylistsResult {
  playlists: VideoPlaylist[];
  loading: boolean;
  refetch: () => void;
}

/** A creator's playlists. Author-filtered. */
export function useVideoPlaylists(pubkey: string | undefined): UseVideoPlaylistsResult {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey
      ? [{ kinds: [PLAYLIST_KIND], authors: [pubkey], '#t': [PLAYLIST_TAG], limit: 100 }]
      : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const playlists = useMemo(() => {
    const byDTag = new Map<string, Event>();
    for (const ev of events) {
      if (pubkey && ev.pubkey !== pubkey) continue;
      const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
      if (!dTag) continue;
      const existing = byDTag.get(dTag);
      if (!existing || ev.created_at > existing.created_at) byDTag.set(dTag, ev);
    }
    return [...byDTag.values()]
      .map(parsePlaylist)
      .filter((p): p is VideoPlaylist => p !== null)
      // A retired playlist has no items; don't list an empty shell.
      .filter(p => p.videoRefs.length > 0)
      .sort((a, b) => b.created_at - a.created_at);
  }, [events, pubkey]);

  return { playlists, loading, refetch };
}

export interface UsePlaylistResult {
  playlist: VideoPlaylist | null;
  /** Resolved videos, in the playlist's own order. */
  videos: BCHVideo[];
  loading: boolean;
}

/**
 * One playlist and its videos.
 *
 * Each referenced video is fetched with its OWN author filter derived from
 * the coordinate, so a playlist can never cause someone else's `d` tag to
 * be attributed to the wrong creator.
 */
export function usePlaylist(
  pubkey: string | undefined, dTag: string | undefined,
): UsePlaylistResult {
  const { events, loading } = useNostrQuery({
    filters: pubkey && dTag
      ? [{ kinds: [PLAYLIST_KIND], authors: [pubkey], '#d': [dTag], limit: 4 }]
      : [],
    enabled: !!pubkey && !!dTag,
    allRelays: true,
  });

  const playlist = useMemo(() => {
    const mine = events.filter(ev =>
      ev.pubkey === pubkey &&
      ev.tags.some(t => t[0] === 'd' && t[1] === dTag));
    if (mine.length === 0) return null;
    const newest = [...mine].sort((a, b) => b.created_at - a.created_at)[0];
    return parsePlaylist(newest);
  }, [events, pubkey, dTag]);

  const refs = useMemo(() => playlist?.videoRefs || [], [playlist]);
  const targets = useMemo(
    () => refs.map(parseVideoCoordinate).filter((t): t is { pubkey: string; dTag: string } => !!t),
    [refs],
  );

  const authors = useMemo(() => [...new Set(targets.map(t => t.pubkey))], [targets]);
  const dTags = useMemo(() => [...new Set(targets.map(t => t.dTag))], [targets]);

  const { events: videoEvents, loading: videosLoading } = useNostrQuery({
    filters: authors.length > 0
      ? [{ kinds: [VIDEO_KIND], authors, '#d': dTags, limit: 300 }]
      : [],
    enabled: authors.length > 0,
    allRelays: true,
  });

  const videos = useMemo(() => {
    // Index by exact coordinate — an `authors` × `#d` filter is a cross
    // product, so we must match both halves, not just the `d` tag.
    const byCoord = new Map<string, Event>();
    for (const ev of videoEvents) {
      const d = ev.tags.find(t => t[0] === 'd')?.[1];
      if (!d) continue;
      const coord = videoCoordinate(ev.pubkey, d);
      const existing = byCoord.get(coord);
      if (!existing || ev.created_at > existing.created_at) byCoord.set(coord, ev);
    }
    // Preserve the playlist's order — it's the creator's editorial choice.
    return refs
      .map(ref => byCoord.get(ref))
      .filter((ev): ev is Event => !!ev)
      .map(parseVideo)
      .filter((v): v is BCHVideo => v !== null);
  }, [videoEvents, refs]);

  return { playlist, videos, loading: loading || (refs.length > 0 && videosLoading) };
}

/** Every published playlist, for the Playlists rail on the Videos page. */
export function useAllPlaylists(): UseVideoPlaylistsResult {
  const { events, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [PLAYLIST_KIND], '#t': [PLAYLIST_TAG], limit: 150 }],
    allRelays: true,
  });

  const playlists = useMemo(() => {
    const byKey = new Map<string, Event>();
    for (const ev of events) {
      const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
      if (!dTag) continue;
      const key = `${ev.pubkey}:${dTag}`;
      const existing = byKey.get(key);
      if (!existing || ev.created_at > existing.created_at) byKey.set(key, ev);
    }
    return [...byKey.values()]
      .map(parsePlaylist)
      .filter((p): p is VideoPlaylist => p !== null)
      .filter(p => p.videoRefs.length > 0)
      .sort((a, b) => b.created_at - a.created_at);
  }, [events]);

  return { playlists, loading, refetch };
}

// ── Channel subscribers ───────────────────────────────────────────

/**
 * Follower counts for a batch of creators.
 *
 * A video channel IS the creator's Nostr identity, so "subscribers" are
 * plain kind-3 followers — no parallel subscription graph. Relay-dependent
 * and therefore approximate; labelled as such in the UI.
 */
export function useCreatorFollowerCounts(pubkeys: string[]): Map<string, number> {
  const key = useMemo(() => [...new Set(pubkeys)].slice(0, 60).join(','), [pubkeys]);
  const targets = useMemo(() => (key ? key.split(',') : []), [key]);

  const { events } = useNostrQuery({
    filters: targets.length > 0
      ? [{ kinds: [3], '#p': targets, limit: 2000 }]
      : [],
    enabled: targets.length > 0,
    allRelays: true,
  });

  return useMemo(() => {
    const sets = new Map<string, Set<string>>();
    const valid = new Set(targets);
    for (const ev of events) {
      for (const tag of ev.tags) {
        // Re-verify the p-tag: a stale contact list that no longer
        // includes the target must not be counted.
        if (tag[0] !== 'p' || !valid.has(tag[1])) continue;
        if (!sets.has(tag[1])) sets.set(tag[1], new Set());
        sets.get(tag[1])!.add(ev.pubkey);
      }
    }
    const out = new Map<string, number>();
    for (const [pk, set] of sets) out.set(pk, set.size);
    return out;
  }, [events, targets]);
}

// ── Recommendations ───────────────────────────────────────────────

export interface UseRecommendationsResult {
  recommended: ScoredVideo[];
  trending: BCHVideo[];
  following: BCHVideo[];
  newCreators: BCHVideo[];
  popular: BCHVideo[];
  engagement: Map<string, VideoEngagement>;
  signals: RecommendationSignals;
}

/**
 * The full set of ranked rails for the Videos home page.
 *
 * All of it is derived from signals the viewer can see the basis of:
 * their own follows, their own click history (device-local), and public
 * Nostr engagement. There is no model, no server-side profile, and nothing
 * that leaves the browser.
 */
export function useRecommendations(videos: BCHVideo[]): UseRecommendationsResult {
  const { user } = useAppContext();
  const { follows } = useFollows(user?.pubkey);

  const creatorPubkeys = useMemo(
    () => [...new Set(videos.map(v => v.pubkey))],
    [videos],
  );
  const creatorFollowers = useCreatorFollowerCounts(creatorPubkeys);
  const engagement = useVideoEngagement(videos);

  const signals = useMemo<RecommendationSignals>(() => ({
    follows: new Set(follows),
    interests: getCategoryInterests(),
    watched: watchedCoordinates(),
    creatorFollowers,
    engagement,
  }), [follows, creatorFollowers, engagement]);

  const recommended = useMemo(
    () => rankRecommended(videos, signals, { limit: 40 }),
    [videos, signals],
  );

  const trending = useMemo(
    () => rankTrending(videos, engagement, { limit: 30 }),
    [videos, engagement],
  );

  const following = useMemo(() => {
    if (follows.length === 0) return [];
    const set = new Set(follows);
    return videos.filter(v => set.has(v.pubkey));
  }, [videos, follows]);

  /**
   * "New Creators" — first-time publishers, so a newcomer's first upload
   * isn't buried under established channels. One video each so the rail
   * shows breadth of people, not depth of catalogue.
   */
  const newCreators = useMemo(() => {
    const counts = new Map<string, number>();
    for (const v of videos) counts.set(v.pubkey, (counts.get(v.pubkey) || 0) + 1);
    const seen = new Set<string>();
    return videos
      .filter(v => (counts.get(v.pubkey) || 0) <= 3)
      .filter(v => {
        if (seen.has(v.pubkey)) return false;
        seen.add(v.pubkey);
        return true;
      })
      .slice(0, 20);
  }, [videos]);

  /**
   * "Popular" — all-time engagement with no recency decay, which is the
   * honest difference from Trending. Still capped per creator.
   */
  const popular = useMemo(() => {
    const scored = videos.map(v => {
      const eng = engagement.get(videoCoordinate(v.pubkey, v.id)) || EMPTY_ENGAGEMENT;
      const score =
        eng.uniqueTippers * 8 + eng.comments * 4 + eng.reactions * 1 +
        Math.log10(1 + eng.tipSats) * 6;
      return { v, score };
    }).filter(s => s.score > 0).sort((a, b) => b.score - a.score);

    const perCreator = new Map<string, number>();
    const out: BCHVideo[] = [];
    for (const { v } of scored) {
      const used = perCreator.get(v.pubkey) || 0;
      if (used >= 3) continue;
      perCreator.set(v.pubkey, used + 1);
      out.push(v);
      if (out.length >= 30) break;
    }
    return out;
  }, [videos, engagement]);

  return { recommended, trending, following, newCreators, popular, engagement, signals };
}

// ── Publishing ────────────────────────────────────────────────────

export interface UseVideoPublishResult {
  /** Publish (or replace) a video. Resolves to the `d` tag on success. */
  addVideo: (draft: VideoDraft) => Promise<string>;
  /** Retire a video. The discussion thread survives; the listing doesn't. */
  removeVideo: (dTag: string) => Promise<void>;
  /** Create or replace a playlist. */
  savePlaylist: (dTag: string, draft: PlaylistDraft) => Promise<void>;
  removePlaylist: (dTag: string) => Promise<void>;
  publishing: boolean;
}

/**
 * Write side of BCH Video.
 *
 * Publishes ONLY metadata. No file is uploaded, no media is transferred,
 * and nothing is written to any BCHNostr-controlled storage.
 */
export function useVideoPublish(): UseVideoPublishResult {
  const { publish } = useNostrPublish();
  const [publishing, setPublishing] = useState(false);

  const addVideo = useCallback(async (draft: VideoDraft): Promise<string> => {
    const built = buildVideoEvent(draft);
    if (!built) throw new Error('That video URL isn’t supported.');
    setPublishing(true);
    try {
      await publish({
        kind: VIDEO_KIND,
        content: built.content,
        tags: built.tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      return built.dTag;
    } finally {
      setPublishing(false);
    }
  }, [publish]);

  const removeVideo = useCallback(async (dTag: string) => {
    setPublishing(true);
    try {
      const { tags, content } = retireVideoEvent(dTag);
      await publish({
        kind: VIDEO_KIND, content, tags,
        created_at: Math.floor(Date.now() / 1000),
      });
    } finally {
      setPublishing(false);
    }
  }, [publish]);

  const savePlaylist = useCallback(async (dTag: string, draft: PlaylistDraft) => {
    setPublishing(true);
    try {
      const { tags, content } = buildPlaylistEvent(dTag, draft);
      await publish({
        kind: PLAYLIST_KIND, content, tags,
        created_at: Math.floor(Date.now() / 1000),
      });
    } finally {
      setPublishing(false);
    }
  }, [publish]);

  const removePlaylist = useCallback(async (dTag: string) => {
    setPublishing(true);
    try {
      const { tags, content } = retirePlaylistEvent(dTag);
      await publish({
        kind: PLAYLIST_KIND, content, tags,
        created_at: Math.floor(Date.now() / 1000),
      });
    } finally {
      setPublishing(false);
    }
  }, [publish]);

  return { addVideo, removeVideo, savePlaylist, removePlaylist, publishing };
}

/** Does the logged-in user already have a video at this URL? */
export function useHasVideoAtUrl(url: string): boolean {
  const { user } = useAppContext();
  const { videos } = useChannelVideos(user?.pubkey);
  const dTag = useMemo(() => (url.trim() ? videoDTag(url) : ''), [url]);
  return !!dTag && videos.some(v => v.id === dTag);
}

// ── Watch history (reactive) ──────────────────────────────────────

/**
 * The local watch history, as React state.
 *
 * Read straight from localStorage — there is no server copy and nothing to
 * sync. `refresh()` re-reads after a clear or a removal.
 */
export function useWatchHistoryState(): {
  entries: HistoryEntry[];
  refresh: () => void;
} {
  const [entries, setEntries] = useState<HistoryEntry[]>(() => getWatchHistory());

  const refresh = useCallback(() => setEntries(getWatchHistory()), []);

  // Another tab may have watched something (or cleared history).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === 'bchnostr:video-history') refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  return { entries, refresh };
}
